import axios from 'axios';
import { getAuthStore, setAuthStore } from '@/services/authStore';

// Always use the configured base URL when provided; fall back to '/api'.
// This respects your explicit base URL in all environments.
const API_BASE = (import.meta.env.VITE_API_BASE as string) || '/api';

const api = axios.create({
  baseURL: API_BASE,
  timeout: 30000,
  withCredentials: true,
});
// Endpoints requiring extended timeouts (in ms)
const LONG_TIMEOUT_ENDPOINTS: Array<{ match: string; timeout: number }> = [
  { match: '/v1/parserbase/dataextractionsandbox', timeout: 180000 }, // Parser sandbox
  { match: '/v1/collector/testportalbot', timeout: 180000 }, // Collector test can also run 1–2 mins
];

// Attach token and optional authUserId from in-memory auth store per request
api.interceptors.request.use((cfg) => {
  try {
    // Apply longer timeout for specific long-running endpoints
    try {
      const url = (cfg.url || '').toString().toLowerCase();
      // Also consider URLs without leading slash (axios sometimes joins differently)
      const normalizedUrl = url.startsWith('/') ? url : `/${url}`;
      const long = LONG_TIMEOUT_ENDPOINTS.find((e) =>
        normalizedUrl.includes(e.match)
      );
      if (long) {
        cfg.timeout = long.timeout;
      }
    } catch (_) {}

    const s = getAuthStore();
    const token = s?.authToken ?? null;
    if (token) {
      (cfg.headers as any) = { ...(cfg.headers as any || {}), Authorization: `Bearer ${token}` };
    }
    // Also include authUserId as a header if present (backend may accept it)
    const authUserId = s?.userData?.authUserId ?? s?.userData?.id ?? null;
    if (authUserId) {
      (cfg.headers as any) = { ...(cfg.headers as any || {}), 'X-Auth-UserId': String(authUserId) };
    }
  } catch (e) {
    // ignore store/header errors
  }
  return cfg;
});

// Refresh-token handling: when a request fails with 401, attempt to refresh the access token
// using an HttpOnly refresh token (sent by the browser as cookie). Queue requests while
// refresh is in progress so we only call refresh once.
let isRefreshing = false as boolean;
let refreshSubscribers: Array<(token: string | null) => void> = [];

function subscribeToken(cb: (token: string | null) => void) {
  refreshSubscribers.push(cb);
}

function onRefreshed(token: string | null) {
  refreshSubscribers.forEach((cb) => cb(token));
  refreshSubscribers = [];
}

api.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = (error?.config || {}) as any;

    // If there's no response or it's not a 401, just reject
    if (!error || !error.response || error.response.status !== 401) {
      return Promise.reject(error);
    }

    // Do NOT attempt refresh for auth endpoints or when no token exists
    try {
      const urlLower = (originalRequest?.url || '').toString().toLowerCase();
      const bypassUrls = [
        '/v1/auth/portallogin',
        '/v1/auth/portal-login-authorization',
        '/v1/auth/signup',
        '/v1/auth/resendotp',
        '/v1/auth/forgot-password',
        '/v1/auth/updatepassword',
        '/v1/auth/change-password',
        '/v1/auth/update-new-password',
      ];
      const shouldBypass = bypassUrls.some((p) => urlLower.includes(p));
      const hasToken = !!getAuthStore()?.authToken;
      if (shouldBypass || !hasToken) {
        return Promise.reject(error);
      }
    } catch (_) {}

    // Avoid retry loops for the refresh endpoint itself
    if (originalRequest._retry) {
      return Promise.reject(error);
    }

    // Mark request for retry
    originalRequest._retry = true;

    // If we're already refreshing, queue this request
    if (isRefreshing) {
      return new Promise((resolve, reject) => {
        subscribeToken((token) => {
          if (!token) return reject(error);
          originalRequest.headers = originalRequest.headers || {};
          originalRequest.headers.Authorization = `Bearer ${token}`;
          resolve(api(originalRequest));
        });
      });
    }

    isRefreshing = true;

    try {
      // Call refresh endpoint using the raw axios instance to avoid re-entering api interceptors.
      // Server should read HttpOnly refresh cookie and return new access token.
  const resp = await axios.post(`${api.defaults.baseURL}/v1/auth/refresh-token`, null, { withCredentials: true });
      const respData = resp?.data || {};
      const newToken = respData?.token || respData?.accessToken || respData?.data?.token || respData?.data?.accessToken || null;

      if (!newToken) {
        // No token returned -> clear and reject
        try { setAuthStore({ authToken: null }); } catch (e) {}
        onRefreshed(null);
        isRefreshing = false;
        // Only notify app to sign out if we previously had a session token
        try {
          if (getAuthStore()?.authToken && typeof window !== 'undefined' && window.dispatchEvent) {
            window.dispatchEvent(new CustomEvent('auth:refresh_failed', { detail: { reason: 'no_token' } }));
          }
        } catch (e) {}
        return Promise.reject(error);
      }

      // Save the new access token and update header defaults
      try {
        (api.defaults.headers as any).Authorization = `Bearer ${newToken}`;
        try { setAuthStore({ authToken: newToken }); } catch (e) {}
      } catch (e) {
        // ignore storage errors
      }

      onRefreshed(newToken);
      isRefreshing = false;

      // Retry the original request with new token
      originalRequest.headers = originalRequest.headers || {};
      originalRequest.headers.Authorization = `Bearer ${newToken}`;
      return api(originalRequest);
    } catch (refreshError) {
      // Refresh failed -> clear token and reject queued requests
      try { setAuthStore({ authToken: null }); } catch (e) {}
      onRefreshed(null);
      isRefreshing = false;
      // Only notify app to sign out if we previously had a session token
      try {
        if (getAuthStore()?.authToken && typeof window !== 'undefined' && window.dispatchEvent) {
          window.dispatchEvent(new CustomEvent('auth:refresh_failed', { detail: { error: refreshError } }));
        }
      } catch (e) {}
      return Promise.reject(refreshError);
    }
  }
);

export function setAuthToken(token?: string | null) {
  try {
    if (token) {
      (api.defaults.headers as any).Authorization = `Bearer ${token}`;
      // update in-memory store
      try { setAuthStore({ authToken: token }); } catch (e) {}
    } else {
      try { setAuthStore({ authToken: null }); } catch (e) {}
      try { delete (api.defaults.headers as any).Authorization; } catch (_) {}
    }
  } catch (e) {
    // ignore
  }
}

export default api;
